import React, { useEffect, useMemo, useState } from 'react';
import { FileText, AlertTriangle, Clock, Signature, FilePlus2, FileCheck, LayoutTemplate, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { useDocumentCategories } from '../../hooks/useDocumentCategories';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import SendTemplateModal from '../../components/template-editor/send-template-modal';

interface DashboardStats {
  draft: number;
  pending: number;
  partially_signed: number;
  completed: number;
  declined: number;
  total: number;
}

interface DashboardData {
  stats: DashboardStats;
  pending_signing_requests_count?: number;
}

interface Template {
  id: string;
  template_title: string;
  category?: { id: string; name: string };
  sent_count: number;
  signer_count: number;
  recipient_count?: number;
  status: string;
}

const PHRASE_COUNT = 12;

const Dashboard: React.FC = () => {
  // State for dashboard data
  const [stats, setStats] = useState<DashboardStats>({
    draft: 0,
    pending: 0,
    partially_signed: 0,
    completed: 0,
    declined: 0,
    total: 0
  });
  const [pendingSigningRequestsCount, setPendingSigningRequestsCount] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [timeFilter, setTimeFilter] = useState<string>('month'); // Default filter is month
  const [categoryFilter, setCategoryFilter] = useState<string>('all'); // Default to all categories
  const { categories } = useDocumentCategories();
  const { t } = useTranslation(['dashboard', 'common']);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [templateToSend, setTemplateToSend] = useState<Template | null>(null);
  const phraseIndex = useMemo(() => Math.floor(Math.random() * PHRASE_COUNT), []);
  const welcomePhrase = t(`dashboard:welcome_phrases.${phraseIndex}`);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Generate document stat cards
  const documentStats = [
    {
      id: '1',
      title: t('dashboard:drafts'),
      count: stats.draft,
      icon: <FileText className="h-5 w-5 text-blue-500" />,
      color: 'blue',
      status: 'draft'
    },
    {
      id: '2',
      title: t('common:status.pending'),
      count: stats.pending,
      icon: <Clock className="h-5 w-5 text-amber-500" />,
      color: 'amber',
      status: 'pending'
    },
    {
      id: '2b',
      title: t('common:status.partially_signed'),
      count: stats.partially_signed,
      icon: <FileCheck className="h-5 w-5 text-orange-500" />,
      color: 'orange',
      status: 'partially_signed'
    },
    {
      id: '3',
      title: t('dashboard:completed'),
      count: stats.completed,
      icon: <FileText className="h-5 w-5 text-green-500" />,
      color: 'green',
      status: 'completed'
    },
    {
      id: '4',
      title: t('dashboard:declined'),
      count: stats.declined,
      icon: <AlertTriangle className="h-5 w-5 text-red-500" />,
      color: 'red',
      status: 'declined'
    },
  ];

  // Handle click on stat card to navigate to Documents with filters
  const handleStatCardClick = (status: string) => {
    // Navigate to documents page with query parameters for filters
    navigate(`/documents?status=${status}&timeFilter=${timeFilter}&categoryId=${categoryFilter}`);
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      // Set default role to 'requestor' if user role is missing
      const role = user?.role || 'requestor';
      const endpoint = role === 'signatory' 
        ? '/api/dashboard/signatory' 
        : '/api/dashboard/requestor';
      
      console.log('Fetching dashboard data from endpoint:', endpoint, 'with filter:', timeFilter, 'category:', categoryFilter);
      
      // Use fetch directly with full URL to ensure proper connection
      // Using centralized API configuration
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(`${apiUrl}${endpoint}?timeFilter=${timeFilter}&categoryId=${categoryFilter}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        throw new Error(`API error: ${response.status}`);
      }
      
      const data: DashboardData = await response.json();
      console.log('Dashboard API response:', data);
      
      if (data) {
        setStats(data.stats);
        setPendingSigningRequestsCount(data.pending_signing_requests_count ?? 0);
        setLastUpdated(new Date());
      }
    } catch (err: any) {
      console.error('Dashboard data error:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/templates?status=active&per_page=6`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        return;
      }
      const data = await response.json();
      setTemplates(data.data ?? []);
    } catch {
      // ignore
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [user, timeFilter, categoryFilter]); // Refetch when filters change

  useEffect(() => {
    fetchTemplates();
  }, [user]);

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Welcome section */}
      <div className="flex items-center p-4 gap-4 rounded-lg shadow relative overflow-hidden" 
           style={{
             backgroundImage: 'url("/assets/images/boreal-lake.png")',
             backgroundSize: 'cover',
             backgroundPosition: 'center'
           }}>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex-grow">
          <h1 className="text-2xl font-bold text-white">{t('dashboard:welcome', { name: user?.first_name || 'User' })}</h1>
          <p className="text-gray-200">{welcomePhrase}</p>
        </div>
      </div>

      {/* Signing Requests card + New Document button */}
      <div className="flex items-center gap-4">
        <Card 
          className="border-l-4 border-teal-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors max-w-xs"
          onClick={() => navigate('/signing-requests')}
        >
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('dashboard:signing_requests')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <p className="text-2xl font-bold">{pendingSigningRequestsCount}</p>
              <Signature className="h-8 w-8 text-teal-500" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('common:status.pending')}</p>
          </CardContent>
        </Card>

        <Button 
          className="bg-green-600 hover:bg-green-700 text-white font-medium shadow-lg transition-all 
                    text-lg py-8 px-8 scale-100 hover:scale-105 transform duration-300"
          onClick={() => navigate('/document-editor')}
        >
          <FilePlus2 className="h-5 w-5 mr-2" />
          {t('dashboard:new_document')}
        </Button>
      </div>

      {/* Templates section */}
      <div className="space-y-4 border rounded-lg p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-indigo-500" />
            {t('dashboard:templates_section')}
          </h2>
          <Button variant="outline" size="sm" onClick={() => navigate('/templates')}>
            {t('dashboard:view_all')}
          </Button>
        </div>

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard:no_active_templates')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="flex flex-col justify-between hover:shadow-md transition-shadow">
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm font-semibold leading-tight line-clamp-2">
                      {template.template_title}
                    </CardTitle>
                    {template.category && (
                      <span className="shrink-0 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full px-2 py-0.5">
                        {template.category.name}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {t('dashboard:times_sent', { count: template.sent_count })}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                    onClick={() => {
                      setTemplateToSend(template);
                      setSendModalOpen(true);
                    }}
                  >
                    <Send className="h-3.5 w-3.5" />
                    {t('dashboard:use_template')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* My Documents section */}
      <div className="space-y-4 border rounded-lg p-5">
        <h2 className="text-xl font-semibold">{t('dashboard:my_documents')}</h2>

        {/* Time filter buttons and category dropdown */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap gap-2 justify-start">
            <Button 
              variant={timeFilter === 'all' ? "default" : "outline"}
              className={`${timeFilter === 'all' ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-500 text-indigo-600 hover:bg-indigo-50'}`}
              onClick={() => setTimeFilter('all')}
            >
              {t('common:time.all_time')}
            </Button>
            <Button 
              variant={timeFilter === 'year' ? "default" : "outline"}
              className={`${timeFilter === 'year' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}`}
              onClick={() => setTimeFilter('year')}
            >
              {t('common:time.year')}
            </Button>
            <Button 
              variant={timeFilter === 'quarter' ? "default" : "outline"}
              className={`${timeFilter === 'quarter' ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-500 text-purple-600 hover:bg-purple-50'}`}
              onClick={() => setTimeFilter('quarter')}
            >
              {t('common:time.quarter')}
            </Button>
            <Button 
              variant={timeFilter === 'month' ? "default" : "outline"}
              className={`${timeFilter === 'month' ? 'bg-green-600 hover:bg-green-700' : 'border-green-500 text-green-600 hover:bg-green-50'}`}
              onClick={() => setTimeFilter('month')}
            >
              {t('common:time.month')}
            </Button>
            <Button 
              variant={timeFilter === 'week' ? "default" : "outline"}
              className={`${timeFilter === 'week' ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}`}
              onClick={() => setTimeFilter('week')}
            >
              {t('common:time.week')}
            </Button>
            <Button 
              variant={timeFilter === 'today' ? "default" : "outline"}
              className={`${timeFilter === 'today' ? 'bg-red-600 hover:bg-red-700' : 'border-red-500 text-red-600 hover:bg-red-50'}`}
              onClick={() => setTimeFilter('today')}
            >
              {t('common:time.today')}
            </Button>
          </div>
          
          <div className="w-full md:w-auto">
            <Select 
              value={categoryFilter} 
              onValueChange={(value) => setCategoryFilter(value)}
            >
              <SelectTrigger className="w-full md:w-[220px]">
                <SelectValue placeholder={t('dashboard:select_category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dashboard:all_categories')}</SelectItem>
                {categories.sort((a, b) => a.name.localeCompare(b.name)).map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {documentStats.map((stat) => (
            <Card 
              key={stat.id} 
              className={`border-l-4 border-${stat.color}-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors`}
              onClick={() => handleStatCardClick(stat.status)}
            >
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.title}</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{stat.count}</p>
                  {stat.icon}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Last updated info */}
      {lastUpdated && (
        <div className="text-sm text-gray-500 dark:text-gray-400 text-right">
          {t('dashboard:last_updated', { time: lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) })}
        </div>
      )}

      {templateToSend && (
        <SendTemplateModal
          open={sendModalOpen}
          onOpenChange={(open) => {
            setSendModalOpen(open);
            if (!open) setTemplateToSend(null);
          }}
          templateId={templateToSend.id}
          templateTitle={templateToSend.template_title}
          recipientCount={templateToSend.recipient_count ?? templateToSend.signer_count}
          onSendComplete={() => {
            setSendModalOpen(false);
            setTemplateToSend(null);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
